import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export default function Login() {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (activeTab === 'login') {
        await login(email, password);
        navigate('/dashboard');
      } else {
        await register(email, password);
        // Após registro, redirecionar para checkout
        navigate('/checkout');
      }
    } catch (err: any) {
      setError(err?.message || 'Erro ao processar solicitação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8 cursor-pointer">
          <img src="/logo-clover.png" alt="Premiei" className="h-12 w-12" />
          <span className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">Premiei</span>
        </Link>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
          {/* Tabs */}
          <div className="flex items-center gap-2 bg-white/5 rounded-full p-1 mb-8">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 px-6 py-3 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'login'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Entrar
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`flex-1 px-6 py-3 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'register'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Criar conta
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-cyan-500/50 focus:bg-white/10 transition-all text-sm"
                placeholder="seu@email.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Senha</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-cyan-500/50 focus:bg-white/10 transition-all text-sm"
                placeholder="••••••••"
                required
              />
            </div>

            {activeTab === 'login' && (
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded cursor-pointer" />
                  <span className="text-white/60">Lembrar de mim</span>
                </label>
                <a href="#" className="text-cyan-400 hover:text-cyan-300 transition-colors cursor-pointer">
                  Esqueceu a senha?
                </a>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-4 text-base font-bold bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl hover:shadow-2xl hover:shadow-cyan-500/40 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processando...' : (activeTab === 'login' ? 'Entrar' : 'Criar Conta')}
            </button>
          </form>

          {/* Info */}
          {activeTab === 'register' && (
            <div className="mt-6 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
              <p className="text-sm text-cyan-300 text-center">
                <i className="ri-information-line mr-1"></i>
                Após criar conta você será direcionado para pagamento para liberar o acesso
              </p>
            </div>
          )}

          {/* Back Link */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-white/60 hover:text-white transition-colors cursor-pointer">
              <i className="ri-arrow-left-line mr-1"></i>
              Voltar para home
            </Link>
          </div>
        </div>

        {/* Social Proof */}
        <div className="mt-8 text-center">
          <p className="text-sm text-white/40">
            <i className="ri-shield-check-line text-green-400 mr-1"></i>
            Seus dados estão seguros e protegidos
          </p>
          <p className="mt-2 text-xs text-white/30">© {new Date().getFullYear()} Premiei</p>
        </div>
      </div>
    </div>
  );
}
